import "./App-header.css";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { confirmLogout } from "../../utils/confirmLogout";
import AdminGlobalSearch from "../globalSearch/AdminGlobalSearch";
import NotificationsDropdown from "./NotificationsDropdown";
import { useNotifications } from "../../context/NotificationsContext";
// Matches the sidebar's icon swap (AppSideBar.js) — same react-icons packs
// the seller app's header/sidebar use.
import { FiBell, FiChevronDown, FiLogOut } from "react-icons/fi";
import { TbLayoutSidebar } from "react-icons/tb";

// Mirrors the labels used in AppSideBar's own links, so the header title
// never drifts from what the sidebar calls the same page.
const PAGE_TITLES = {
  "/welcomeAdmin": "Dashboard",
  "/customerOrders": "Orders Management",
  "/create-order": "Create Order",
  "/order-cancellation-requests": "Orders Cancellation",
  "/cancelled-orders": "Cancelled Orders",
  "/customerRequests": "Sourcing Requests",
  "/create-sourcing-request": "Create Request",
  "/restock-requests": "Restock Requests",
  "/inventory-cancellation-requests": "Requests Cancellation",
  "/customerProducts": "Inventory List",
  "/inventory-deletion-requests": "Deletion Requests",
  "/deleted-products": "Deleted Products",
  "/customerList": "End-Customers List",
  "/createNewCustomer": "Add New End-Customer",
  "/customerWalletTopUp": "Manual Adjustment",
  "/history": "Activity History",
  "/global-ledger": "Transactions History",
  "/topup-requests": "Top-Up Requests",
  "/notifications": "Notifications",
  "/sellers": "My Sellers",
  "/allSellers": "All Sellers",
  "/subscriptionRequests": "Subscription Requests",
  "/integrations": "Connected Stores",
  "/integrationHeldOrders": "Held Orders",
  "/integrationJobs": "Sync Tasks",
  "/send-to-zq": "Send to ZQ",
  "/integrationSyncLog": "Sync Activity",
};

function AppHeader({ isMobileOpen, setIsMobileOpen, onOpenRelated }) {
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { badgeCount, dismissBadge } = useNotifications();

  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  // Below the chrome breakpoint the search collapses to an icon that expands
  // over the header row rather than disappearing entirely.
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  /**
   * Whether the sidebar stays open, or expands only while hovered (the default).
   *
   * The value is written to the document root and read from there by CSS alone --
   * that is the whole design, so this control can live wherever it reads best
   * without anything being passed between components. It sat in the sidebar
   * footer before, and moving it here cost three lines and no plumbing.
   *
   * A separate attribute from data-theme, which this app hardcodes to "dark" in
   * public/index.html and must keep.
   */
  const [pinned, setPinned] = useState(() => localStorage.getItem("sidebar-pinned") === "1");

  useEffect(() => {
    document.documentElement.setAttribute("data-sidebar", pinned ? "pinned" : "hover");
    localStorage.setItem("sidebar-pinned", pinned ? "1" : "0");
  }, [pinned]);

  useEffect(() => {
    if (!notifOpen) return;
    const handleOutsideClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [notifOpen]);

  useEffect(() => {
    if (!profileMenuOpen) return;
    const handleOutsideClick = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) setProfileMenuOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [profileMenuOpen]);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch (error) {}
    }
  }, []);

  const pageTitle = PAGE_TITLES[location.pathname] || "Dashboard";

  const logout = () => {
    setProfileMenuOpen(false);
    confirmLogout(navigate);
  };

  const initials = (user?.full_name || "Admin User")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return (
    <header className="users-header">
      <div className="left-header">
        <button
          type="button"
          className={`animated-hamburger ${isMobileOpen ? "is-active" : ""}`}
          onClick={() => setIsMobileOpen((prev) => !prev)}
          aria-label={isMobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMobileOpen}
        >
          <span></span><span></span><span></span>
        </button>

        <div className="current-path">
          {/* Left of the title, where every editor puts a panel toggle -- and it
              points at the rail it controls. Unlike the seller app, .current-path
              here has no .desktop-only, and this header keeps its title on a
              phone -- so the button needs its own display:none in the 1000px
              block, or it would offer to pin a drawer. */}
          <button
            type="button"
            className="sidebar-pin-toggle"
            onClick={() => setPinned((prev) => !prev)}
            aria-pressed={pinned}
            title={pinned ? "Let the menu collapse" : "Keep the menu open"}
          >
            <TbLayoutSidebar />
          </button>

          <span className="path-text">{pageTitle}</span>
        </div>
      </div>

      <div className={`center-header ${mobileSearchOpen ? "search-open" : ""}`}>
        <AdminGlobalSearch />
      </div>

      <div className="header-right-group">
        <button
          type="button"
          className="icon-btn-round mobile-search-toggle"
          onClick={() => setMobileSearchOpen((prev) => !prev)}
          aria-label={mobileSearchOpen ? "Close search" : "Open search"}
        >
          <i className={`fas fa-${mobileSearchOpen ? "times" : "search"}`}></i>
        </button>

        <div className="notification-wrapper-container" ref={notifRef}>
          <button
            type="button"
            className="icon-btn-round notification-btn"
            aria-label="Notifications"
            onClick={() => {
              // Computed from current render state and applied as a plain side
              // effect — calling dismissBadge() (another component's setState)
              // from inside setNotifOpen's updater would fire it during this
              // component's render phase, which React rejects.
              const next = !notifOpen;
              setNotifOpen(next);
              // Clears the count on open without marking anything read — the
              // notifications stay unread until actually opened or acted on.
              if (next) dismissBadge();
            }}
          >
            <FiBell />
            {badgeCount > 0 && (
              <span className="badge-count">{badgeCount > 99 ? "99+" : badgeCount}</span>
            )}
          </button>
          {notifOpen && <NotificationsDropdown onClose={() => setNotifOpen(false)} onOpenRelated={onOpenRelated} />}
        </div>

        <div className="profile-wrapper-container" ref={profileMenuRef}>
          <button
            type="button"
            className="profile-pill"
            onClick={() => setProfileMenuOpen((prev) => !prev)}
            aria-expanded={profileMenuOpen}
          >
            <span className="profile-avatar">{initials}</span>
            <span className="profile-info">
              <strong>{user ? user.full_name : "Admin User"}</strong>
              <small>{user?.is_super_admin ? "Super Admin" : "Administrator"}</small>
            </span>
            <FiChevronDown className={`dropdown-icon ${profileMenuOpen ? "is-open" : ""}`} />
          </button>

          {profileMenuOpen && (
            <div className="dropdown-panel profile-dropdown-menu">
              <button type="button" className="dropdown-item danger" onClick={logout}>
                <FiLogOut /> Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default AppHeader;
